import React, { useState, useEffect, useCallback } from 'react';

// API configuration
const getApiBase = () => {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api/e2e';
  }
  return 'http://localhost:8021';
};

const API_BASE = getApiBase();

// Color constants
const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-500' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500' },
  low: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-500' },
  info: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-500' },
};

const STATUS_COLORS = {
  passed: { bg: 'bg-green-100', text: 'text-green-800' },
  failed: { bg: 'bg-red-100', text: 'text-red-800' },
  running: { bg: 'bg-blue-100', text: 'text-blue-800' },
  pending: { bg: 'bg-gray-100', text: 'text-gray-800' },
  error: { bg: 'bg-red-100', text: 'text-red-800' },
  open: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  resolved: { bg: 'bg-green-100', text: 'text-green-800' },
  ignored: { bg: 'bg-gray-100', text: 'text-gray-800' },
};

// Helper components
const TimeAgo = ({ date }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  if (!date) return <span className="text-gray-500">-</span>;

  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let text;
  if (diffMins < 1) text = 'just now';
  else if (diffMins < 60) text = `${diffMins}m ago`;
  else if (diffHours < 24) text = `${diffHours}h ago`;
  else text = `${diffDays}d ago`;

  return <span className="text-gray-500 text-sm">{text}</span>;
};

const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const colors = STATUS_COLORS[variant] || SEVERITY_COLORS[variant] || { bg: 'bg-gray-100', text: 'text-gray-800' };
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  return (
    <span className={`inline-flex items-center rounded-full font-medium ${colors.bg} ${colors.text} ${sizeClass}`}>
      {children}
    </span>
  );
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow border border-gray-200 ${className}`}>
    {children}
  </div>
);

const StatCard = ({ title, value, subtitle, icon, trend, warning }) => (
  <Card className={`p-4 ${warning ? 'border-yellow-400 border-2' : ''}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {icon && <div className="text-3xl text-gray-400">{icon}</div>}
    </div>
    {trend !== undefined && (
      <div className={`mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% from last period
      </div>
    )}
    {warning && (
      <div className="mt-2 text-sm text-yellow-700 bg-yellow-50 px-2 py-1 rounded">
        {warning}
      </div>
    )}
  </Card>
);

// Alert Banner
const AlertBanner = ({ type = 'warning', children, onDismiss }) => {
  const colors = {
    warning: 'bg-yellow-50 border-yellow-400 text-yellow-800',
    error: 'bg-red-50 border-red-400 text-red-800',
    info: 'bg-blue-50 border-blue-400 text-blue-800',
    success: 'bg-green-50 border-green-400 text-green-800',
  };

  return (
    <div className={`border-l-4 p-4 mb-4 ${colors[type]}`}>
      <div className="flex justify-between items-start">
        <div>{children}</div>
        {onDismiss && (
          <button onClick={onDismiss} className="ml-4 text-lg font-bold opacity-50 hover:opacity-100">
            ×
          </button>
        )}
      </div>
    </div>
  );
};

// Edit App Modal
const EditAppModal = ({ app, onClose, onSave }) => {
  const [domain, setDomain] = useState(app?.domain || '');
  const [sentryId, setSentryId] = useState(app?.sentry_project_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/apps/${app.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: domain || undefined,
          sentry_project_id: sentryId || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update app');
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!app) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit App: {app.name}</h3>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
              {!app.has_crawlable_domain && (
                <span className="ml-2 text-yellow-600 text-xs">(Required for E2E tests)</span>
              )}
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter the production domain (e.g., myapp.com). Required for E2E test generation.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sentry Project ID
              {!app.sentry_project_id && (
                <span className="ml-2 text-yellow-600 text-xs">(Required for error monitoring)</span>
              )}
            </label>
            <input
              type="text"
              value={sentryId}
              onChange={(e) => setSentryId(e.target.value)}
              placeholder="4510201193299968"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Sentry project ID for error correlation.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Tab components
const TabButton = ({ active, onClick, children, count, warning }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 font-medium text-sm rounded-t-lg border-b-2 transition-colors relative ${
      active
        ? 'border-blue-500 text-blue-600 bg-blue-50'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
    {count !== undefined && (
      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${active ? 'bg-blue-100' : 'bg-gray-100'}`}>
        {count}
      </span>
    )}
    {warning && (
      <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
    )}
  </button>
);

// Main Dashboard Component
const E2ETestingDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [overview, setOverview] = useState(null);
  const [apps, setApps] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [testRuns, setTestRuns] = useState([]);
  const [findings, setFindings] = useState([]);
  const [correlations, setCorrelations] = useState([]);
  const [sentryErrors, setSentryErrors] = useState([]);

  // UI state
  const [editingApp, setEditingApp] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  // Action state
  const [generatingTests, setGeneratingTests] = useState({});
  const [runningTests, setRunningTests] = useState({});
  const [syncing, setSyncing] = useState(false);

  // Computed values
  const incompleteApps = apps.filter(app => !app.has_crawlable_domain || !app.sentry_project_id);
  const hasIncompleteApps = incompleteApps.length > 0 && !dismissedAlerts.includes('incomplete');

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, appsRes, suitesRes, runsRes, findingsRes, correlationsRes, errorsRes] = await Promise.all([
        fetch(`${API_BASE}/api/dashboard/overview`),
        fetch(`${API_BASE}/api/apps`),
        fetch(`${API_BASE}/api/tests/suites?limit=20`),
        fetch(`${API_BASE}/api/tests/runs?limit=20`),
        fetch(`${API_BASE}/api/findings?limit=50`),
        fetch(`${API_BASE}/api/correlations?limit=20`),
        fetch(`${API_BASE}/api/sentry-errors?limit=50`),
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (appsRes.ok) setApps(await appsRes.json());
      if (suitesRes.ok) setTestSuites(await suitesRes.json());
      if (runsRes.ok) setTestRuns(await runsRes.json());
      if (findingsRes.ok) setFindings(await findingsRes.json());
      if (correlationsRes.ok) setCorrelations(await correlationsRes.json());
      if (errorsRes.ok) setSentryErrors(await errorsRes.json());

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Actions
  const syncApps = async () => {
    setSyncing(true);
    try {
      await fetch(`${API_BASE}/api/apps/sync`, { method: 'POST' });
      await fetchData();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const generateTests = async (appId) => {
    setGeneratingTests(prev => ({ ...prev, [appId]: true }));
    try {
      const response = await fetch(`${API_BASE}/api/tests/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app_id: appId }),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.detail || 'Failed to generate tests');
      }
      await fetchData();
    } catch (err) {
      console.error('Generate tests failed:', err);
    } finally {
      setGeneratingTests(prev => ({ ...prev, [appId]: false }));
    }
  };

  const runTests = async (suiteId) => {
    setRunningTests(prev => ({ ...prev, [suiteId]: true }));
    try {
      await fetch(`${API_BASE}/api/tests/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suite_id: suiteId }),
      });
      await fetchData();
    } catch (err) {
      console.error('Run tests failed:', err);
    } finally {
      setRunningTests(prev => ({ ...prev, [suiteId]: false }));
    }
  };

  const updateFindingStatus = async (findingId, status) => {
    try {
      await fetch(`${API_BASE}/api/findings/${findingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      await fetchData();
    } catch (err) {
      console.error('Update finding failed:', err);
    }
  };

  // Render tabs
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Incomplete Apps Alert */}
      {hasIncompleteApps && (
        <AlertBanner
          type="warning"
          onDismiss={() => setDismissedAlerts(prev => [...prev, 'incomplete'])}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">⚠️</span>
            <div>
              <p className="font-semibold">
                {incompleteApps.length} app{incompleteApps.length > 1 ? 's have' : ' has'} incomplete configuration
              </p>
              <p className="text-sm mt-1">
                Apps need a production domain for E2E tests and Sentry ID for error monitoring.
                <button
                  onClick={() => setActiveTab('apps')}
                  className="ml-2 underline font-medium"
                >
                  Configure now
                </button>
              </p>
            </div>
          </div>
        </AlertBanner>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Apps"
          value={overview?.active_apps || 0}
          subtitle={`${overview?.crawlable_apps || 0} ready for E2E`}
          icon="📱"
          warning={overview?.incomplete_apps > 0 ? `${overview.incomplete_apps} need config` : null}
        />
        <StatCard
          title="Test Suites"
          value={overview?.test_suites || 0}
          icon="🧪"
        />
        <StatCard
          title="Pass Rate (24h)"
          value={`${overview?.recent_runs?.pass_rate || 0}%`}
          subtitle={`${overview?.recent_runs?.total || 0} runs`}
          icon="✅"
        />
        <StatCard
          title="Open Findings"
          value={overview?.open_findings_total || 0}
          icon="⚠️"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Test Runs */}
        <Card>
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Recent Test Runs</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {testRuns.slice(0, 5).map(run => (
              <div key={run.id} className="p-4 flex items-center justify-between">
                <div>
                  <Badge variant={run.status}>{run.status}</Badge>
                  <p className="text-sm text-gray-600 mt-1">
                    Suite {run.suite_id.slice(0, 8)}...
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm">
                    <span className="text-green-600">{run.passed_count} passed</span>
                    {' / '}
                    <span className="text-red-600">{run.failed_count} failed</span>
                  </p>
                  <TimeAgo date={run.created_at} />
                </div>
              </div>
            ))}
            {testRuns.length === 0 && (
              <div className="p-4 text-gray-500 text-center">No test runs yet</div>
            )}
          </div>
        </Card>

        {/* Recent Findings */}
        <Card>
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Recent Findings</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {findings.filter(f => f.status === 'open').slice(0, 5).map(finding => (
              <div key={finding.id} className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={finding.severity}>{finding.severity}</Badge>
                  <Badge variant={finding.type}>{finding.type}</Badge>
                </div>
                <p className="text-sm text-gray-900 font-medium">{finding.title}</p>
                <TimeAgo date={finding.created_at} />
              </div>
            ))}
            {findings.filter(f => f.status === 'open').length === 0 && (
              <div className="p-4 text-gray-500 text-center">No open findings</div>
            )}
          </div>
        </Card>
      </div>

      {/* Correlations */}
      {correlations.length > 0 && (
        <Card>
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Test-Error Correlations</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {correlations.slice(0, 5).map(corr => (
              <div key={corr.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge variant={corr.correlation_type}>{corr.correlation_type}</Badge>
                    <p className="text-sm text-gray-600 mt-1">{corr.explanation}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-blue-600">
                      {(corr.confidence * 100).toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-500">confidence</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  const renderApps = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Monitored Applications</h2>
        <button
          onClick={syncApps}
          disabled={syncing}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {syncing ? 'Syncing...' : 'Sync Apps'}
        </button>
      </div>

      {incompleteApps.length > 0 && (
        <AlertBanner type="warning">
          <p className="font-medium">Configuration Required</p>
          <p className="text-sm mt-1">
            {incompleteApps.length} app{incompleteApps.length > 1 ? 's are' : ' is'} missing domain or Sentry configuration.
            Click the Edit button to configure.
          </p>
        </AlertBanner>
      )}

      <Card>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">App</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sentry</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {apps.map(app => {
              const isIncomplete = !app.has_crawlable_domain || !app.sentry_project_id;
              return (
                <tr key={app.id} className={isIncomplete ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {isIncomplete && <span className="text-yellow-500">⚠️</span>}
                      <span className="font-medium text-gray-900">{app.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {app.has_crawlable_domain ? (
                      <span className="text-gray-700">{app.domain}</span>
                    ) : (
                      <span className="text-yellow-600 text-sm">
                        {app.domain} <span className="text-xs">(not accessible)</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {app.sentry_project_id ? (
                      <Badge variant="info">{app.sentry_project_id.slice(0, 12)}...</Badge>
                    ) : (
                      <span className="text-yellow-600 text-sm">Not configured</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={app.is_active ? 'passed' : 'error'}>
                      {app.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => setEditingApp(app)}
                      className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => generateTests(app.id)}
                      disabled={generatingTests[app.id] || !app.has_crawlable_domain}
                      title={!app.has_crawlable_domain ? 'Configure a valid domain first' : ''}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generatingTests[app.id] ? 'Generating...' : 'Generate Tests'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {apps.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No apps found. Click "Sync Apps" to pull from Project Management.
          </div>
        )}
      </Card>
    </div>
  );

  const renderTestSuites = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Test Suites</h2>

      <Card>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Suite</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pages</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tests</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {testSuites.map(suite => (
              <tr key={suite.id}>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{suite.name}</p>
                  <p className="text-xs text-gray-500">{suite.id.slice(0, 8)}...</p>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={suite.status}>{suite.status}</Badge>
                </td>
                <td className="px-4 py-3 text-gray-600">{suite.pages_crawled}</td>
                <td className="px-4 py-3 text-gray-600">{suite.tests_count}</td>
                <td className="px-4 py-3">
                  <TimeAgo date={suite.created_at} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => runTests(suite.id)}
                    disabled={runningTests[suite.id] || suite.status !== 'passed'}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {runningTests[suite.id] ? 'Running...' : 'Run Tests'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {testSuites.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No test suites yet. Generate tests from the Apps tab.
          </div>
        )}
      </Card>
    </div>
  );

  const renderFindings = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Findings</h2>

      <Card>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {findings.map(finding => (
              <tr key={finding.id}>
                <td className="px-4 py-3">
                  <Badge variant={finding.severity}>{finding.severity}</Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge>{finding.type}</Badge>
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{finding.title}</p>
                  {finding.description && (
                    <p className="text-xs text-gray-500 truncate max-w-md">{finding.description}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={finding.status}>{finding.status}</Badge>
                </td>
                <td className="px-4 py-3">
                  <TimeAgo date={finding.created_at} />
                </td>
                <td className="px-4 py-3 text-right">
                  {finding.status === 'open' && (
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => updateFindingStatus(finding.id, 'resolved')}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Resolve
                      </button>
                      <button
                        onClick={() => updateFindingStatus(finding.id, 'ignored')}
                        className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Ignore
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {findings.length === 0 && (
          <div className="p-8 text-center text-gray-500">No findings yet</div>
        )}
      </Card>
    </div>
  );

  const renderSentryErrors = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Sentry Errors</h2>

      <Card>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Events</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Seen</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sentryErrors.map(error => (
              <tr key={error.id}>
                <td className="px-4 py-3">
                  <Badge variant={error.level === 'error' ? 'high' : 'medium'}>
                    {error.level}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{error.title}</p>
                  {error.culprit && (
                    <p className="text-xs text-gray-500">{error.culprit}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{error.event_count}</td>
                <td className="px-4 py-3">
                  <TimeAgo date={error.last_seen} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sentryErrors.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No Sentry errors cached. Errors will appear after the next poll cycle.
          </div>
        )}
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading E2E Testing Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold">Error loading dashboard</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">E2E Testing Agent</h1>
          <p className="text-gray-500">Automated test generation and error correlation</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </TabButton>
          <TabButton
            active={activeTab === 'apps'}
            onClick={() => setActiveTab('apps')}
            count={apps.length}
            warning={incompleteApps.length > 0}
          >
            Apps
          </TabButton>
          <TabButton
            active={activeTab === 'suites'}
            onClick={() => setActiveTab('suites')}
            count={testSuites.length}
          >
            Test Suites
          </TabButton>
          <TabButton
            active={activeTab === 'findings'}
            onClick={() => setActiveTab('findings')}
            count={findings.filter(f => f.status === 'open').length}
          >
            Findings
          </TabButton>
          <TabButton
            active={activeTab === 'sentry'}
            onClick={() => setActiveTab('sentry')}
            count={sentryErrors.length}
          >
            Sentry Errors
          </TabButton>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'apps' && renderApps()}
        {activeTab === 'suites' && renderTestSuites()}
        {activeTab === 'findings' && renderFindings()}
        {activeTab === 'sentry' && renderSentryErrors()}
      </div>

      {/* Edit App Modal */}
      {editingApp && (
        <EditAppModal
          app={editingApp}
          onClose={() => setEditingApp(null)}
          onSave={() => {
            setEditingApp(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default E2ETestingDashboard;
